import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Link2, Mail, Building2 } from 'lucide-react';
import { getAzureConfig } from '../../lib/api';
import { FortnoxConfigTab } from './AdminSettingsPage';

// ── Brevo section ─────────────────────────────────────────────────────────────

function BrevoSection() {
    const { t } = useTranslation();
    return (
        <Card>
            <CardHeader>
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <Mail className="w-5 h-5 text-primary" />
                    Brevo (Email)
                </CardTitle>
                <CardDescription>
                    {t('integrations.brevo_note')}
                </CardDescription>
            </CardHeader>
            <CardContent>
                <p className="text-sm text-muted-foreground">
                    {t('integrations.brevo_note')}{' '}
                    <Link
                        to="/dashboard/settings?tab=notifications&solo=true"
                        className="text-primary underline-offset-2 hover:underline"
                    >
                        {t('integrations.brevo_settings_link')}
                    </Link>
                    .
                </p>
            </CardContent>
        </Card>
    );
}

// ── Microsoft 365 section ─────────────────────────────────────────────────────

function maskId(value) {
    if (!value) return null;
    return value.slice(0, 8) + '...';
}

function Ms365Section({ azureConfig, loading }) {
    const { t } = useTranslation();

    const isEnabled = Boolean(azureConfig?.client_id);

    return (
        <Card>
            <CardHeader>
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-primary" />
                    Microsoft 365
                </CardTitle>
                <CardDescription>
                    {t('integrations.ms365_note')}
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
                {loading ? (
                    <div className="flex items-center h-8">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary" />
                    </div>
                ) : (
                    <>
                        <div className="flex items-center gap-2">
                            <Badge variant={isEnabled ? 'default' : 'secondary'}>
                                {isEnabled
                                    ? t('integrations.ms365_status_enabled')
                                    : t('integrations.ms365_status_disabled')}
                            </Badge>
                        </div>

                        {isEnabled ? (
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                                <div>
                                    <p className="text-xs uppercase text-muted-foreground font-medium mb-1">
                                        {t('integrations.ms365_client_id')}
                                    </p>
                                    <p className="text-sm font-mono bg-muted px-3 py-1.5 rounded">
                                        {maskId(azureConfig?.client_id) ?? '—'}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-xs uppercase text-muted-foreground font-medium mb-1">
                                        {t('integrations.ms365_tenant_id')}
                                    </p>
                                    <p className="text-sm font-mono bg-muted px-3 py-1.5 rounded">
                                        {maskId(azureConfig?.tenant_id) ?? '—'}
                                    </p>
                                </div>
                                {azureConfig?.allowed_domain && (
                                    <div>
                                        <p className="text-xs uppercase text-muted-foreground font-medium mb-1">
                                            {t('integrations.ms365_allowed_domain')}
                                        </p>
                                        <p className="text-sm font-mono bg-muted px-3 py-1.5 rounded">
                                            {azureConfig.allowed_domain}
                                        </p>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <p className="text-sm text-muted-foreground">
                                {t('integrations.ms365_not_configured')}
                            </p>
                        )}

                        <p className="text-xs text-muted-foreground border-t pt-3 mt-2">
                            {t('integrations.ms365_platform_note')}
                        </p>
                    </>
                )}
            </CardContent>
        </Card>
    );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function IntegrationsPage() {
    const { t } = useTranslation();
    const [azureConfig, setAzureConfig] = useState(null);
    const [azureLoading, setAzureLoading] = useState(true);

    useEffect(() => {
        getAzureConfig()
            .then((r) => setAzureConfig(r.data))
            .catch(() => setAzureConfig(null))
            .finally(() => setAzureLoading(false));
    }, []);

    return (
        <div className="space-y-6">
            <div>
                <h1 className="font-heading text-4xl font-bold tracking-tight">
                    {t('integrations.title')}
                </h1>
                <p className="text-muted-foreground mt-1">
                    {t('integrations.subtitle')}
                </p>
            </div>

            <Tabs defaultValue="fortnox" className="space-y-4">
                <TabsList>
                    <TabsTrigger value="fortnox" className="flex items-center gap-2">
                        <Link2 className="w-4 h-4" />
                        Fortnox
                    </TabsTrigger>
                    <TabsTrigger value="brevo" className="flex items-center gap-2">
                        <Mail className="w-4 h-4" />
                        Brevo
                    </TabsTrigger>
                    <TabsTrigger value="ms365" className="flex items-center gap-2">
                        <Building2 className="w-4 h-4" />
                        Microsoft 365
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="fortnox">
                    {/* TODO: wire to FortnoxConfigTab — shows connection, credentials, salary codes, absence codes, GL accounts, invoice settings */}
                    <FortnoxConfigTab />
                </TabsContent>

                <TabsContent value="brevo">
                    <BrevoSection />
                </TabsContent>

                <TabsContent value="ms365">
                    <Ms365Section azureConfig={azureConfig} loading={azureLoading} />
                </TabsContent>
            </Tabs>
        </div>
    );
}
